import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import ProgressBar from '../components/ui/ProgressBar'
import Modal from '../components/ui/Modal'
import DRLProgressBars from '../components/etapa0/DRLProgressBars'
import {
  Plus, Building2, TrendingUp, CheckCircle2, Clock, Search,
  Pencil, Trash2, AlertTriangle, Calendar, CalendarDays, Users, Copy,
} from 'lucide-react'

export default function DashboardPage() {
  const { isAdmin, isNVC, profile } = useAuth()
  const navigate = useNavigate()

  const [consultancies, setConsultancies]         = useState([])
  const [overdueTasks, setOverdueTasks]           = useState([])
  const [upcomingTasks, setUpcomingTasks]         = useState([])
  const [progressMap, setProgressMap]             = useState({})
  const [consultantWorkload, setConsultantWorkload] = useState([])
  const [loading, setLoading]                     = useState(true)

  const [showCreate, setShowCreate] = useState(false)
  const [search, setSearch]         = useState('')
  const [creating, setCreating]     = useState(false)
  const [form, setForm] = useState({
    name: '', client_company: '', industry: '', description: '', start_date: '', end_date: '',
    country: '', company_size: '', years_in_market: '', annual_revenue: '', main_products: '',
  })
  // Template cloning
  const [cloneEnabled, setCloneEnabled] = useState(false)
  const [cloneSourceId, setCloneSourceId] = useState('')

  const [editOpen, setEditOpen]     = useState(false)
  const [editTarget, setEditTarget] = useState(null)
  const [editForm, setEditForm]     = useState({
    name: '', client_company: '', industry: '', description: '', status: 'active', start_date: '', end_date: '',
  })
  const [savingEdit, setSavingEdit] = useState(false)

  useEffect(() => { fetchData() }, [])

  async function fetchData() {
    setLoading(true)
    const today  = new Date().toISOString().split('T')[0]
    const in7    = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]

    const [consRes, overdueRes, upcomingRes, tasksRes, workloadRes] = await Promise.all([
      supabase
        .from('consultancies')
        .select('*, profiles!consultancies_created_by_fkey(full_name)')
        .order('created_at', { ascending: false }),

      supabase
        .from('tasks')
        .select('id, name, due_date, status, sections(name, phase:phases(name, consultancy_id))')
        .lt('due_date', today)
        .neq('status', 'completed')
        .order('due_date'),

      supabase
        .from('tasks')
        .select('id, name, due_date, status, sections(name, phase:phases(name, consultancy_id))')
        .gte('due_date', today)
        .lte('due_date', in7)
        .neq('status', 'completed')
        .order('due_date'),

      supabase
        .from('tasks')
        .select('id, status, sections(phases(consultancy_id))'),

      supabase
        .from('profiles')
        .select('id, full_name, consultancy_users(consultancy_id)')
        .eq('role', 'consultant'),
    ])

    setConsultancies(consRes.data ?? [])
    setOverdueTasks(overdueRes.data ?? [])
    setUpcomingTasks(upcomingRes.data ?? [])

    // Compute task completion % per consultancy
    const pMap = {}
    ;(tasksRes.data ?? []).forEach(t => {
      const cid = t.sections?.phases?.consultancy_id
      if (!cid) return
      if (!pMap[cid]) pMap[cid] = { total: 0, done: 0 }
      pMap[cid].total++
      if (t.status === 'completed') pMap[cid].done++
    })
    setProgressMap(pMap)

    setConsultantWorkload(workloadRes.data ?? [])
    setLoading(false)
  }

  async function createConsultancy(e) {
    e.preventDefault()
    setCreating(true)
    try {
      const { data: cons, error } = await supabase
        .from('consultancies')
        .insert({
          ...form,
          created_by: profile.id,
          start_date: form.start_date || null,
          end_date:   form.end_date   || null,
        })
        .select()
        .single()
      if (error) throw error

      if (cloneEnabled && cloneSourceId) {
        // Clone structure from source consultancy
        await cloneConsultancyStructure(cloneSourceId, cons.id)
      } else {
        const { error: stagesError } = await supabase.rpc('create_project_stages', {
          p_consultancy_id: cons.id,
        })
        if (stagesError) throw stagesError
      }

      setShowCreate(false)
      setForm({ name: '', client_company: '', industry: '', description: '', start_date: '', end_date: '', country: '', company_size: '', years_in_market: '', annual_revenue: '', main_products: '' })
      setCloneEnabled(false)
      setCloneSourceId('')
      navigate(`/consultancy/${cons.id}`)
    } catch (err) {
      console.error(err)
      alert('Error al crear la consultoría: ' + err.message)
    } finally {
      setCreating(false)
    }
  }

  async function cloneConsultancyStructure(sourceId, targetId) {
    // 1. Fetch source phases (project-level)
    const { data: srcPhases } = await supabase
      .from('phases')
      .select('*, sections(*, tasks(*))')
      .eq('consultancy_id', sourceId)
      .is('business_unit_id', null)
      .order('phase_order')

    for (const ph of (srcPhases ?? [])) {
      const { data: newPhase } = await supabase
        .from('phases')
        .insert({ name: ph.name, description: ph.description, phase_order: ph.phase_order, consultancy_id: targetId, status: 'pending', progress: 0 })
        .select().single()
      if (!newPhase) continue

      for (const sec of (ph.sections ?? [])) {
        const { data: newSec } = await supabase
          .from('sections')
          .insert({ name: sec.name, description: sec.description, phase_id: newPhase.id, status: 'pending', section_order: sec.section_order })
          .select().single()
        if (!newSec) continue

        for (const task of (sec.tasks ?? [])) {
          await supabase.from('tasks').insert({
            name: task.name, description: task.description, priority: task.priority,
            section_id: newSec.id, status: 'pending', created_by: profile.id,
          })
        }
      }
    }

    // 2. Clone BUs
    const { data: srcBus } = await supabase
      .from('business_units')
      .select('*, phases(*, sections(*, tasks(*)))')
      .eq('consultancy_id', sourceId)
      .order('bu_order')

    for (const bu of (srcBus ?? [])) {
      const { data: newBu } = await supabase
        .from('business_units')
        .insert({ name: bu.name, description: bu.description, consultancy_id: targetId, bu_order: bu.bu_order })
        .select().single()
      if (!newBu) continue

      for (const ph of (bu.phases ?? [])) {
        const { data: newPhase } = await supabase
          .from('phases')
          .insert({ name: ph.name, description: ph.description, phase_order: ph.phase_order, consultancy_id: targetId, business_unit_id: newBu.id, status: 'pending', progress: 0 })
          .select().single()
        if (!newPhase) continue

        for (const sec of (ph.sections ?? [])) {
          const { data: newSec } = await supabase
            .from('sections')
            .insert({ name: sec.name, description: sec.description, phase_id: newPhase.id, status: 'pending', section_order: sec.section_order })
            .select().single()
          if (!newSec) continue

          for (const task of (sec.tasks ?? [])) {
            await supabase.from('tasks').insert({
              name: task.name, description: task.description, priority: task.priority,
              section_id: newSec.id, status: 'pending', created_by: profile.id,
            })
          }
        }
      }
    }
  }

  function openEdit(e, c) {
    e.stopPropagation()
    setEditTarget(c)
    setEditForm({
      name:           c.name,
      client_company: c.client_company,
      industry:       c.industry    ?? '',
      description:    c.description ?? '',
      status:         c.status      ?? 'active',
      start_date:     c.start_date  ?? '',
      end_date:       c.end_date    ?? '',
    })
    setEditOpen(true)
  }

  async function saveEdit(e) {
    e.preventDefault()
    setSavingEdit(true)
    const payload = {
      ...editForm,
      start_date: editForm.start_date || null,
      end_date:   editForm.end_date   || null,
    }
    await supabase.from('consultancies').update(payload).eq('id', editTarget.id)
    setConsultancies(prev => prev.map(c => c.id === editTarget.id ? { ...c, ...payload } : c))
    setEditOpen(false)
    setSavingEdit(false)
  }

  async function deleteConsultancy(e, c) {
    e.stopPropagation()
    if (!confirm(`¿Eliminar la consultoría "${c.name}" y todos sus datos? Esta acción no se puede deshacer.`)) return
    await supabase.from('consultancies').delete().eq('id', c.id)
    setConsultancies(prev => prev.filter(x => x.id !== c.id))
  }

  const filtered = consultancies.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.client_company.toLowerCase().includes(search.toLowerCase())
  )

  const stats = {
    total:     consultancies.length,
    active:    consultancies.filter(c => c.status === 'active').length,
    completed: consultancies.filter(c => c.status === 'completed').length,
    paused:    consultancies.filter(c => c.status === 'paused').length,
  }

  const totalTasks = Object.values(progressMap).reduce((s, v) => s + v.total, 0)
  const doneTasks  = Object.values(progressMap).reduce((s, v) => s + v.done,  0)
  const completionPct = totalTasks > 0 ? Math.round((doneTasks / totalTasks) * 100) : 0

  function getProgress(consultancyId) {
    const p = progressMap[consultancyId]
    if (!p || p.total === 0) return 0
    return Math.round((p.done / p.total) * 100)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-500 text-sm mt-0.5">Visión general de todas las consultorías</p>
        </div>
        {isAdmin && (
          <button
            onClick={() => setShowCreate(true)}
            className="btn-primary flex items-center gap-2 whitespace-nowrap text-sm"
          >
            <Plus size={16} /> Nueva Consultoría
          </button>
        )}
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
        {[
          { label: 'Total',       value: stats.total,       icon: Building2,    color: 'text-brand-600',  bg: 'bg-brand-50'  },
          { label: 'Activas',     value: stats.active,      icon: TrendingUp,   color: 'text-blue-600',   bg: 'bg-blue-50'   },
          { label: 'Completadas', value: stats.completed,   icon: CheckCircle2, color: 'text-green-600',  bg: 'bg-green-50'  },
          { label: 'Pausadas',    value: stats.paused,      icon: Clock,        color: 'text-yellow-600', bg: 'bg-yellow-50' },
          { label: 'Tareas vencidas', value: overdueTasks.length, icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-50' },
          { label: 'Completado',  value: `${completionPct}%`, icon: CheckCircle2, color: 'text-emerald-600', bg: 'bg-emerald-50' },
        ].map(({ label, value, icon: Icon, color, bg }) => (
          <div key={label} className="card">
            <div className="flex items-center gap-2.5">
              <div className={`p-2 rounded-xl ${bg} shrink-0`}>
                <Icon size={18} className={color} />
              </div>
              <div>
                <div className="text-xl font-bold text-gray-900">{value}</div>
                <div className="text-xs text-gray-500 leading-tight">{label}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Consultant Workload — visible to NVC staff */}
      {isNVC && consultantWorkload.length > 0 && (
        <div className="card">
          <div className="flex items-center gap-2 mb-4">
            <Users size={16} className="text-brand-600" />
            <h2 className="font-semibold text-gray-800 text-sm">Carga de trabajo por consultor</h2>
          </div>
          <div className="flex flex-wrap gap-3">
            {consultantWorkload.map(c => {
              const count = c.consultancy_users?.length ?? 0
              return (
                <div key={c.id} className="flex items-center gap-2.5 bg-gray-50 rounded-xl px-4 py-2.5 border border-gray-100">
                  <div className="w-8 h-8 rounded-full bg-brand-700 flex items-center justify-center text-white text-sm font-bold shrink-0">
                    {c.full_name?.charAt(0) ?? '?'}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-800 leading-tight">{c.full_name}</p>
                    <p className="text-xs text-gray-400">
                      {count} consultor{count !== 1 ? 'ías' : 'ía'} asignada{count !== 1 ? 's' : ''}
                    </p>
                  </div>
                  <span className={`ml-2 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold
                    ${count === 0 ? 'bg-gray-200 text-gray-400' : count >= 4 ? 'bg-red-100 text-red-600' : 'bg-brand-100 text-brand-700'}`}>
                    {count}
                  </span>
                </div>
              )
            })}
            {consultantWorkload.length === 0 && (
              <p className="text-sm text-gray-400">No hay consultores registrados</p>
            )}
          </div>
        </div>
      )}

      {/* Overdue Tasks Panel */}
      {overdueTasks.length > 0 && (
        <div className="card border-red-200 bg-red-50/50">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle size={16} className="text-red-500" />
            <h2 className="font-semibold text-red-700 text-sm">
              {overdueTasks.length} tarea{overdueTasks.length !== 1 ? 's' : ''} vencida{overdueTasks.length !== 1 ? 's' : ''}
            </h2>
          </div>
          <div className="space-y-2">
            {overdueTasks.slice(0, 5).map(t => (
              <div key={t.id} className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-red-100">
                <div>
                  <p className="text-sm font-medium text-gray-800">{t.name}</p>
                  <p className="text-xs text-gray-400">{t.sections?.name} · {t.sections?.phase?.name}</p>
                </div>
                <span className="text-xs text-red-600 font-medium whitespace-nowrap ml-3">
                  Venció {new Date(t.due_date + 'T00:00:00').toLocaleDateString('es', { day: 'numeric', month: 'short' })}
                </span>
              </div>
            ))}
            {overdueTasks.length > 5 && (
              <p className="text-xs text-red-500 text-center">+{overdueTasks.length - 5} más</p>
            )}
          </div>
        </div>
      )}

      {/* Upcoming Tasks Panel */}
      {upcomingTasks.length > 0 && (
        <div className="card border-amber-200 bg-amber-50/40">
          <div className="flex items-center gap-2 mb-3">
            <CalendarDays size={16} className="text-amber-500" />
            <h2 className="font-semibold text-amber-700 text-sm">
              {upcomingTasks.length} tarea{upcomingTasks.length !== 1 ? 's' : ''} próxima{upcomingTasks.length !== 1 ? 's' : ''} esta semana
            </h2>
          </div>
          <div className="space-y-2">
            {upcomingTasks.slice(0, 5).map(t => (
              <div key={t.id} className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-amber-100">
                <div>
                  <p className="text-sm font-medium text-gray-800">{t.name}</p>
                  <p className="text-xs text-gray-400">{t.sections?.name} · {t.sections?.phase?.name}</p>
                </div>
                <span className="text-xs text-amber-600 font-medium whitespace-nowrap ml-3">
                  Vence {new Date(t.due_date + 'T00:00:00').toLocaleDateString('es', { day: 'numeric', month: 'short' })}
                </span>
              </div>
            ))}
            {upcomingTasks.length > 5 && (
              <p className="text-xs text-amber-500 text-center">+{upcomingTasks.length - 5} más</p>
            )}
          </div>
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="input pl-9 max-w-md"
          placeholder="Buscar por nombre o empresa..."
        />
      </div>

      {/* Consultancies grid */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-brand-500 border-t-transparent" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 text-gray-400">
          <Building2 size={48} className="mx-auto mb-3 opacity-30" />
          <p className="font-medium">No hay consultorías todavía</p>
          {isAdmin && <p className="text-sm mt-1">Crea la primera haciendo clic en "Nueva Consultoría"</p>}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {filtered.map(c => (
            <ConsultancyCard
              key={c.id}
              consultancy={c}
              isAdmin={isAdmin}
              progress={getProgress(c.id)}
              taskStats={progressMap[c.id]}
              onClick={() => navigate(`/consultancy/${c.id}`)}
              onEdit={e => openEdit(e, c)}
              onDelete={e => deleteConsultancy(e, c)}
            />
          ))}
        </div>
      )}

      {/* Create modal */}
      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="Nueva Consultoría" size="md">
        <form onSubmit={createConsultancy} className="space-y-4">
          <div>
            <label className="label">Nombre del proyecto *</label>
            <input className="input" required value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="ej. Plan Estratégico 2025" />
          </div>
          <div>
            <label className="label">Empresa cliente *</label>
            <input className="input" required value={form.client_company}
              onChange={e => setForm(f => ({ ...f, client_company: e.target.value }))}
              placeholder="ej. Grupo Empresarial XYZ" />
          </div>
          <div>
            <label className="label">Industria</label>
            <input className="input" value={form.industry}
              onChange={e => setForm(f => ({ ...f, industry: e.target.value }))}
              placeholder="ej. Consumo masivo, Retail, Servicios financieros..." />
          </div>

          {/* ── Contexto para el Agente Estratégico ── */}
          <div className="border border-indigo-100 bg-indigo-50/50 rounded-xl p-3 space-y-3">
            <p className="text-xs font-semibold text-indigo-700 flex items-center gap-1.5">
              🤖 Contexto para el Agente IA <span className="font-normal text-indigo-500">(enriquece los análisis automáticos)</span>
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label text-xs">País(es) principal(es) *</label>
                <input className="input" value={form.country}
                  onChange={e => setForm(f => ({ ...f, country: e.target.value }))}
                  placeholder="ej. Colombia, México, Perú..." />
              </div>
              <div>
                <label className="label text-xs">Tamaño de la empresa</label>
                <select className="input" value={form.company_size}
                  onChange={e => setForm(f => ({ ...f, company_size: e.target.value }))}>
                  <option value="">Seleccionar...</option>
                  <option value="startup">Startup (&lt;10 empleados)</option>
                  <option value="pyme">Pyme (10–100 empleados)</option>
                  <option value="mediana">Mediana (100–500 empleados)</option>
                  <option value="grande">Grande (500–5.000 empleados)</option>
                  <option value="multinacional">Multinacional (&gt;5.000 empleados)</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label text-xs">Años en el mercado</label>
                <input type="number" min="0" className="input" value={form.years_in_market}
                  onChange={e => setForm(f => ({ ...f, years_in_market: e.target.value }))}
                  placeholder="ej. 15" />
              </div>
              <div>
                <label className="label text-xs">Ventas anuales aprox.</label>
                <input className="input" value={form.annual_revenue}
                  onChange={e => setForm(f => ({ ...f, annual_revenue: e.target.value }))}
                  placeholder="ej. USD 50M, COP 200.000M..." />
              </div>
            </div>
            <div>
              <label className="label text-xs">Principales productos o servicios</label>
              <textarea className="input resize-none" rows={2} value={form.main_products}
                onChange={e => setForm(f => ({ ...f, main_products: e.target.value }))}
                placeholder="ej. Bebidas carbonatadas, jugos y agua embotellada. Principal marca: XYZ..." />
            </div>
          </div>

          <div>
            <label className="label">Descripción / Objetivo del proyecto</label>
            <textarea className="input resize-none" rows={2} value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Objetivo principal de esta consultoría..." />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Fecha de inicio</label>
              <input type="date" className="input" value={form.start_date}
                onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} />
            </div>
            <div>
              <label className="label">Fecha de cierre</label>
              <input type="date" className="input" value={form.end_date}
                onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} />
            </div>
          </div>
          {/* Template cloning */}
          {consultancies.length > 0 && (
            <div className="border border-gray-200 rounded-xl p-3 space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={cloneEnabled}
                  onChange={e => { setCloneEnabled(e.target.checked); setCloneSourceId('') }}
                  className="w-4 h-4 accent-brand-600"
                />
                <span className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                  <Copy size={14} className="text-brand-600" /> Clonar estructura de otra consultoría
                </span>
              </label>
              {cloneEnabled && (
                <select
                  className="input mt-1"
                  value={cloneSourceId}
                  onChange={e => setCloneSourceId(e.target.value)}
                  required={cloneEnabled}
                >
                  <option value="">Selecciona la consultoría fuente...</option>
                  {consultancies.map(c => (
                    <option key={c.id} value={c.id}>{c.name} — {c.client_company}</option>
                  ))}
                </select>
              )}
              {cloneEnabled && (
                <p className="text-xs text-gray-400">Se copiarán etapas, secciones y tareas (sin archivos ni comentarios).</p>
              )}
            </div>
          )}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={() => setShowCreate(false)} className="btn-secondary flex-1">Cancelar</button>
            <button type="submit" disabled={creating} className="btn-primary flex-1">
              {creating ? (cloneEnabled ? 'Clonando...' : 'Creando...') : 'Crear Consultoría'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Edit consultancy modal */}
      <Modal open={editOpen} onClose={() => setEditOpen(false)} title="Editar Consultoría" size="md">
        <form onSubmit={saveEdit} className="space-y-4">
          <div>
            <label className="label">Nombre del proyecto *</label>
            <input className="input" required value={editForm.name}
              onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} />
          </div>
          <div>
            <label className="label">Empresa cliente *</label>
            <input className="input" required value={editForm.client_company}
              onChange={e => setEditForm(f => ({ ...f, client_company: e.target.value }))} />
          </div>
          <div>
            <label className="label">Industria</label>
            <input className="input" value={editForm.industry}
              onChange={e => setEditForm(f => ({ ...f, industry: e.target.value }))} />
          </div>
          <div>
            <label className="label">Descripción</label>
            <textarea className="input resize-none" rows={3} value={editForm.description}
              onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Fecha de inicio</label>
              <input type="date" className="input" value={editForm.start_date}
                onChange={e => setEditForm(f => ({ ...f, start_date: e.target.value }))} />
            </div>
            <div>
              <label className="label">Fecha de cierre</label>
              <input type="date" className="input" value={editForm.end_date}
                onChange={e => setEditForm(f => ({ ...f, end_date: e.target.value }))} />
            </div>
          </div>
          <div>
            <label className="label">Estado</label>
            <select className="input" value={editForm.status}
              onChange={e => setEditForm(f => ({ ...f, status: e.target.value }))}>
              <option value="active">Activa</option>
              <option value="paused">Pausada</option>
              <option value="completed">Completada</option>
            </select>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={() => setEditOpen(false)} className="btn-secondary flex-1">Cancelar</button>
            <button type="submit" disabled={savingEdit} className="btn-primary flex-1">
              {savingEdit ? 'Guardando...' : 'Guardar cambios'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  )
}

function ConsultancyCard({ consultancy, isAdmin, progress, taskStats, onClick, onEdit, onDelete }) {
  const statusColors = { active: 'bg-green-500', paused: 'bg-yellow-500', completed: 'bg-brand-500' }
  const statusLabels = { active: 'Activa', paused: 'Pausada', completed: 'Completada' }
  const today    = new Date().toISOString().split('T')[0]
  const isOverdue = consultancy.end_date && consultancy.end_date < today && consultancy.status !== 'completed'

  return (
    <div className="card hover:shadow-md hover:border-brand-300 transition-all group">
      <button onClick={onClick} className="w-full text-left">
        {/* Header row */}
        <div className="flex items-start justify-between mb-3">
          <div className="w-10 h-10 rounded-xl bg-brand-700 flex items-center justify-center text-white font-bold text-lg shrink-0">
            {consultancy.client_company.charAt(0)}
          </div>
          <div className="flex items-center gap-1.5">
            <span className={`w-2 h-2 rounded-full ${statusColors[consultancy.status]}`} />
            <span className="text-xs text-gray-500">{statusLabels[consultancy.status]}</span>
          </div>
        </div>

        {/* Title */}
        <h3 className="font-semibold text-gray-900 group-hover:text-brand-700 transition-colors mb-0.5">
          {consultancy.name}
        </h3>
        <p className="text-sm text-gray-500 mb-2">{consultancy.client_company}</p>

        {consultancy.industry && (
          <p className="text-xs text-gray-400 mb-2">{consultancy.industry}</p>
        )}

        {/* Dates */}
        {(consultancy.start_date || consultancy.end_date) && (
          <div className="flex items-center gap-1 text-xs text-gray-400 mb-2">
            <Calendar size={11} />
            {consultancy.start_date && (
              <span>{new Date(consultancy.start_date + 'T00:00:00').toLocaleDateString('es', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
            )}
            {consultancy.start_date && consultancy.end_date && <span>→</span>}
            {consultancy.end_date && (
              <span>{new Date(consultancy.end_date + 'T00:00:00').toLocaleDateString('es', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
            )}
          </div>
        )}

        {/* Overdue badge */}
        {isOverdue && (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-red-600 bg-red-50 px-2 py-0.5 rounded-full mb-2">
            <AlertTriangle size={11} /> Atrasado
          </span>
        )}

        {/* Progress bar */}
        <div className="mt-2">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-gray-400">Progreso</span>
            <span className="text-xs font-medium text-gray-600">
              {taskStats ? `${taskStats.done}/${taskStats.total} tareas` : '—'}
            </span>
          </div>
          <ProgressBar value={progress} size="sm" />
        </div>

        {/* DRL inicial vs final (compacto) */}
        <div className="mt-2">
          <DRLProgressBars consultancyId={consultancy.id} compact />
        </div>
      </button>

      {/* Admin actions */}
      {isAdmin && (
        <div className="flex gap-1 justify-end mt-3 pt-3 border-t border-gray-100">
          <button onClick={onEdit}
            className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-gray-400 hover:text-brand-600 hover:bg-brand-50 rounded-lg transition-colors">
            <Pencil size={13} /> Editar
          </button>
          <button onClick={onDelete}
            className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
            <Trash2 size={13} /> Eliminar
          </button>
        </div>
      )}
    </div>
  )
}
